'use client'
import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { IconButton } from "@/components/scirix/icon-button"
import { cn } from "@/lib/utils"

/**
 * Height of the top close-button strip. Exposed on every DialogContent as
 * `--dialog-close-strip-h` so dialogs that need to do scroll math or sticky
 * layouts can subtract it. Most consumers can ignore this — the strip sits
 * inline at the top of the content column.
 */
const DIALOG_CLOSE_STRIP_HEIGHT = "calc(var(--space-md) + 2rem)"

const Dialog = DialogPrimitive.Root
const DialogTrigger = DialogPrimitive.Trigger
const DialogClose = DialogPrimitive.Close

const DialogPortal = ({ children, ...props }: DialogPrimitive.DialogPortalProps) => (
  <DialogPrimitive.Portal {...props}>{children}</DialogPrimitive.Portal>
)
DialogPortal.displayName = DialogPrimitive.Portal.displayName

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-[color-mix(in_oklab,var(--token-surface-scrim)_78%,transparent)] backdrop-blur-[6px] data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const dialogSizeClasses: Record<"sm" | "md" | "lg", string> = {
  sm: "max-w-[min(520px,calc(100vw-2*var(--space-md)))]",
  md: "max-w-[min(640px,calc(100vw-2*var(--space-md)))]",
  lg: "max-w-[min(720px,calc(100vw-2*var(--space-md)))]",
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    size?: "sm" | "md" | "lg"
    /**
     * Extra classes for the DialogOverlay rendered behind this content. Lets
     * a specific dialog override the default scrim — e.g. swap in a stronger
     * `backdrop-blur` — without touching every other dialog in the app.
     */
    overlayClassName?: string
    /**
     * Hide the auto-rendered close button at the top of the dialog. Use only
     * when the dialog must be dismissed via an explicit action (e.g. a
     * destructive confirmation that requires a deliberate cancel/confirm
     * click).
     */
    hideClose?: boolean
    /**
     * Accessible label for the auto-rendered close button. Defaults to
     * "Close". Override to give context, e.g. "Close new task dialog".
     */
    closeLabel?: string
    /**
     * Classes applied to the body wrapper that holds `children`. Default body
     * padding is `p-[var(--space-lg)]` (reduced top when the close strip is
     * visible). Pass `"p-0"` here to opt fully out of body padding when the
     * children own their own layout.
     */
    bodyClassName?: string
  }
>(({ className, children, size = "md", overlayClassName, hideClose = false, closeLabel = "Close", bodyClassName, style, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay className={overlayClassName} />
    <DialogPrimitive.Content
      ref={ref}
      style={{
        ["--dialog-close-strip-h" as string]: DIALOG_CLOSE_STRIP_HEIGHT,
        ...style,
      }}
      className={cn(
        // `flex flex-col` so the optional close-strip sits as the first row
        // at the top of the dialog and the body fills the remaining height.
        // Default padding (`p-[var(--space-lg)]`) wraps the body wrapper, not
        // the outer Content — that way `className="p-0"` from callers cleanly
        // disables body padding while the close strip keeps its own.
        "fixed left-1/2 top-1/2 z-50 flex w-[calc(100vw-2*var(--space-md))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-[var(--radius-giga)] border border-[color-mix(in_oklab,var(--token-border-default)_80%,transparent)] bg-[color-mix(in_oklab,var(--token-surface-default)_94%,transparent)] shadow-[var(--shadow-xl)] outline-none data-[state=open]:fade-in-90 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95",
        dialogSizeClasses[size],
        className,
      )}
      {...props}
    >
      {!hideClose ? (
        <div className="flex shrink-0 justify-end px-[var(--space-md)] pt-[var(--space-md)]">
          <DialogPrimitive.Close asChild>
            <IconButton ariaLabel={closeLabel} icon={X} />
          </DialogPrimitive.Close>
        </div>
      ) : null}
      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col",
          // Default body padding. Top is reduced when the close strip is
          // visible so its own pt-md already provides the vertical breathing
          // room — otherwise default-padded dialogs end up with ~28px above
          // the title. Callers can opt out of all of this via `bodyClassName`.
          !hideClose ? "px-[var(--space-lg)] pb-[var(--space-lg)] pt-[var(--space-sm)]" : "p-[var(--space-lg)]",
          bodyClassName,
        )}
      >
        {children}
      </div>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("space-y-[var(--space-xs)] text-left", className)} {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "mt-[var(--space-md)] flex flex-col gap-[var(--space-sm)] border-t border-border/60 pt-[var(--space-md)] sm:flex-row sm:items-center sm:justify-end",
      className,
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
